// @flow
//
// Shared signup submission lifecycle used by both legacy /register and the new
// /signup route. Centralizes the conversion-critical path so the two surfaces
// can never drift on backend payload, Lead tracking, or post-signup redirect.
//
// Responsibilities:
//   - Coupon validation (?code=)
//   - planId capture (?planid=) → Redux billing slice
//   - referralCode capture (?referralCode=) → localStorage + backend log
//   - Deduped field-focus tracking
//   - registerNewAccount submission with backend payload + click IDs
//   - Lead event tracking (Facebook Pixel + Google Ads)
//   - Firebase login + redirect to /verifyPhone

import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import queryString from 'query-string';

import SPBackend from '../services/SPBackend';
import adTracking from '../services/adTracking';
import { login } from '../reduxStore/modules/user/actions';
import { setSnackMessage } from '../reduxStore/modules/common/actions';
import { log, splitFullName } from '../utils/jsUtils';

const useSignupSubmission = () => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const parsed = queryString.parse(location.search);
  const { planid: paramPlanId, referralCode, code: couponCode } = parsed;

  const isSignupEnabled = process.env.REACT_APP_SIGNUP_ENABLED === 'true';

  const [couponValidation, setCouponValidation] = useState({
    loading: false,
    valid: null,
    error: null,
    couponCode: null,
    couponInfo: null
  });

  // First visit: log referral link visit to backend (once per browser session).
  useEffect(() => {
    if (typeof referralCode !== 'undefined' && referralCode !== null && referralCode.length > 0) {
      log('[useSignupSubmission] referralCode param:', referralCode);
      const storageRefCode = window.localStorage.getItem('neoRefCode');
      if (storageRefCode === null) {
        window.localStorage.setItem('neoRefCode', referralCode);
        SPBackend.logReferralLinkVisit(referralCode)
          .then((resp) => {
            log('[useSignupSubmission] logReferralLinkVisit response:', resp);
          })
          .catch((e) => {
            log('[useSignupSubmission] logReferralLinkVisit error:', e);
          });
      }
    }
  }, []); // run once on mount

  // planId from query → Redux billing slice.
  useEffect(() => {
    if (paramPlanId && paramPlanId !== '') {
      dispatch({
        type: 'ACTION_SET_BILLING_PLANID',
        planId: paramPlanId
      });
    }
  }, [paramPlanId, dispatch]);

  // Coupon validation effect.
  useEffect(() => {
    if (couponCode && couponCode.length > 0) {
      log('[useSignupSubmission] Coupon code detected:', couponCode);
      setCouponValidation((prev) => ({
        ...prev,
        loading: true,
        couponCode: couponCode,
        error: null
      }));

      SPBackend.validateRegistrationCoupon(couponCode)
        .then((response) => {
          log('[useSignupSubmission] validateRegistrationCoupon response:', response);
          const { valid, coupon, message } = response.data;
          setCouponValidation({
            loading: false,
            valid: valid,
            error: valid ? null : (message || 'Invalid code'),
            couponCode: couponCode,
            couponInfo: valid ? coupon : null
          });
        })
        .catch((error) => {
          log('[useSignupSubmission] validateRegistrationCoupon error:', error);
          setCouponValidation({
            loading: false,
            valid: false,
            error: 'Unable to validate code. Please try again.',
            couponCode: couponCode,
            couponInfo: null
          });
        });
    }
  }, [couponCode]);

  const canShowForm = isSignupEnabled || couponValidation.valid === true;

  // Deduped first-focus tracker. Lazy-init avoids allocating a Set every render.
  const focusedFieldsRef = useRef(null);
  if (focusedFieldsRef.current === null) {
    focusedFieldsRef.current = new Set();
  }
  const trackFieldFocus = useCallback((fieldName) => {
    if (focusedFieldsRef.current.has(fieldName)) return;
    focusedFieldsRef.current.add(fieldName);
    adTracking.trackFieldFocus(fieldName);
  }, []);

  /**
   * submit(registration)
   *   registration: { fullName, email, password, phone, companyName, agreed }
   *
   * Two-phase: first registerNewAccount, then authenticate + redirect. Each
   * phase has its own error path matching the legacy Signup.js behavior:
   *   - register error → snack with backend message, re-throw
   *   - auth error → snack ("created but auth failed"), re-throw
   * Returns { message } from the backend on success.
   */
  const submit = useCallback(async(registration) => {
    log('[useSignupSubmission][submit] registration:', registration);
    const storageRefCode = window.localStorage.getItem('neoRefCode') || '';
    const { fullName, ...rest } = registration;
    const { firstName, lastName } = splitFullName(fullName);

    let response;
    try {
      response = await SPBackend.registerNewAccount({
        ...rest,
        firstName,
        lastName,
        agreed: registration.agreed,
        referralCode: storageRefCode,
        ...adTracking.getAllClickIds()
      });
    } catch (error) {
      const { response: errResp } = error;
      const errorMessage = typeof errResp?.data === 'string'
        ? errResp.data
        : errResp?.data?.message || 'Registration failed. Please check your information and try again.';
      dispatch(setSnackMessage({
        message: errorMessage,
        level: 'error'
      }));
      log('[useSignupSubmission][submit] register error:', errResp);
      throw error;
    }

    try {
      window.localStorage.removeItem('neoRefCode');
      // Differentiate the V2 2-step `/signup` flow from the legacy 1-step
      // `/register` flow so they're independently attributable in Google Ads
      // and Meta Events Manager (`content_name`).
      if (location.pathname === '/signup') {
        adTracking.trackSignupStep(2);
      } else {
        adTracking.trackLegacySignup();
      }
      // Dispatch the login thunk directly with the credentials the user just
      // submitted. Going through useUserAuth.authenticate() here would call a
      // separate hook instance whose email/password state is empty, since the
      // page component holds its own useUserAuth.
      const loginResult = await dispatch(login(registration.email, registration.password));
      if (!loginResult || loginResult.success !== true) {
        throw new Error(loginResult?.error || 'Authentication failed');
      }
      log('[useSignupSubmission][submit] account created!', response);
      history.push('/verifyPhone');
      return { message: response?.data?.message };
    } catch (authError) {
      log('[useSignupSubmission][submit] authentication error:', authError);
      dispatch(setSnackMessage({
        message: 'Account created successfully, but authentication failed. Please try logging in.',
        level: 'error'
      }));
      throw authError;
    }
  }, [dispatch, history, location.pathname]);

  return {
    couponValidation,
    couponCode,
    isSignupEnabled,
    canShowForm,
    trackFieldFocus,
    submit
  };
};

export default useSignupSubmission;
